import config from '../../config';

import { Face } from '../../types';

import {
  getAccessTokenClient,
  getApiKeyClient,
  getAttendeeClient,
} from '../client/http';

const {
  apiBaseUrl: { elements: baseUrl },
} = config;

const accessTokenClient = () => getAccessTokenClient(baseUrl);
const apiKeyClient = () => getApiKeyClient(baseUrl);
const attendeeClient = () => getAttendeeClient(baseUrl);

const getClient = (isPublic?: boolean, isAttendee?: boolean) => {
  const isPublicUpd = isPublic || isAttendee;

  let client = accessTokenClient;
  if (isPublicUpd) client = isAttendee ? attendeeClient : apiKeyClient;

  return client;
};

export const createFace = async (
  eventId: string,
  personData: Partial<Face>,
  isPublic?: boolean,
  isAttendee?: boolean,
) => {
  try {
    const client = getClient(isPublic, isAttendee);
    const response = await client().post('api/face', {
      eventId,
      ...personData,
    });

    return response.data.face;
  } catch (err: any) {
    throw new Error('Error creating person');
  }
};

export const updateFace = async (props: {
  eventId: string;
  elementId: string;
  personData: Partial<Face>;
  isPublic?: boolean;
  isAttendee?: boolean;
}) => {
  const { eventId, elementId, personData, isPublic, isAttendee } = props;

  try {
    const client = getClient(isPublic, isAttendee);
    const updFace = { eventId, elementId, ...personData, modified: new Date() };
    const response = await client().put('api/face', updFace);

    return response.data.face;
  } catch (err: any) {
    throw new Error('Error updating person');
  }
};

export const deleteFace = async (elementId: string, isPublic?: boolean) => {
  try {
    await (isPublic ? apiKeyClient : accessTokenClient)().delete(
      `api/face/${elementId}`,
    );
  } catch (err: any) {
    throw new Error(`Error deleting face: ${err.message}`);
  }
};

export const getUploadedFaces = async (eventId: string, isPublic?: boolean) => {
  try {
    const response = await (isPublic ? apiKeyClient : accessTokenClient)().get(
      `api/faces?eventId=${eventId}`,
    );
    return response.data.faces;
  } catch (err: any) {
    throw new Error('Error fetching uploaded faces', err);
  }
};

export const getFaceByEmail = async (
  eventId: string,
  email: string,
  isAttendee?: boolean,
) => {
  try {
    const client = isAttendee ? attendeeClient : accessTokenClient;
    const emailUpd = email.replaceAll('+', '%2B');
    const response = await client().get(
      `api/event/${eventId}/face?email=${emailUpd}`,
    );

    return response.data;
  } catch (err: any) {
    if (err.response?.status === 404) return null;
    throw new Error('Error fetching person');
  }
};

export const getFaceById = async (faceId: string, isAttendee?: boolean) => {
  try {
    const client = isAttendee ? attendeeClient : accessTokenClient;
    const response = await client().get(`api/faces/${faceId}`);

    return response.data.face;
  } catch (err: any) {
    if (err.response?.status === 404) return null;
    throw new Error('Error fetching person');
  }
};

export const getFaceResults = async (eventId: string) => {
  try {
    const response = await accessTokenClient().get(
      `api/face-results?eventId=${eventId}`,
    );
    return response.data;
  } catch (err: any) {
    if (err.response.data.message === 'There are no video templates') {
      return null;
    } else
      throw new Error(
        `Error getting face results: ${
          err.response.data.message || err.message
        }`,
      );
  }
};
